import { GQL } from '@aline/graphql';

import { MimeType } from './constants';

export const DOCUMENT_STATUS_SKIP_REASON = {
  AttachedToWorkflow: 'ATTACHED_TO_WORKFLOW',
  CannotSignTemplate: 'CANNOT_SIGN_TEMPLATE',
  NotAuthorized: 'NOT_AUTHORIZED',
  NotPdf: 'NOT_PDF',
  SignedOrCertificate: 'SIGNED_OR_CERTIFICATE',
  SigningWithinAline: 'SIGNING_WITHIN_ALINE',
  WorkflowSentForSignature: 'WORKFLOW_SENT_FOR_SIGNATURE',
} as const;

export type DocumentStatusSkipReason =
  (typeof DOCUMENT_STATUS_SKIP_REASON)[keyof typeof DOCUMENT_STATUS_SKIP_REASON];

export interface DocumentStatusSubject {
  readonly isActive: boolean;
  readonly isCertificate: boolean;
  readonly isSigned: boolean;
  readonly variant: GQL.DocumentVariant;
}

/**
 * Workflow facts the manual-signed rule needs.
 * A general file on a signing workflow is not blocked. `wasSentForSig` matches the model flag.
 */
export interface DocumentSigningWorkflowContext {
  readonly isSignedOutsideAline: boolean;
  readonly isSigningFile: boolean;
  readonly wasSentForSig: boolean;
}

export interface DocumentStatusWorkflowContext extends DocumentSigningWorkflowContext {
  readonly envelopeStatus: GQL.EnvelopeStatus;
}

/**
 * Signing is done once the envelope completes (AlineSign, DocuSign) or signing outside Aline
 * finishes (Signed). Voiding or reverting the envelope moves it back out of these states.
 */
const IS_SIGNING_COMPLETE_BY_ENVELOPE_STATUS = {
  [GQL.EnvelopeStatus.Completed]: true,
  [GQL.EnvelopeStatus.Created]: false,
  [GQL.EnvelopeStatus.Declined]: false,
  [GQL.EnvelopeStatus.Delivered]: false,
  [GQL.EnvelopeStatus.Drafted]: false,
  [GQL.EnvelopeStatus.Sent]: false,
  [GQL.EnvelopeStatus.Signed]: true,
  [GQL.EnvelopeStatus.Voided]: false,
} satisfies Record<GQL.EnvelopeStatus, boolean>;

export type DocumentStatusChangeEligibility =
  | { readonly outcome: 'apply' }
  | { readonly outcome: 'noop' }
  | { readonly outcome: 'skip'; readonly reason: DocumentStatusSkipReason };

/**
 * Fields written for a target status.
 * `isSigned` and `variant` are null for Expired, which only flips `isActive`.
 */
export interface DocumentStatusFieldWrite {
  readonly isActive: boolean;
  readonly isSigned: boolean | null;
  readonly status: GQL.DocumentStatus;
  readonly variant: GQL.DocumentVariant | null;
}

export function getComputedDocumentStatus(document: DocumentStatusSubject): GQL.DocumentStatus {
  if (!document.isActive) return GQL.DocumentStatus.Expired;
  if (document.isCertificate || document.isSigned) return GQL.DocumentStatus.Signed;
  if (document.variant === GQL.DocumentVariant.Template) return GQL.DocumentStatus.Template;

  return GQL.DocumentStatus.Draft;
}

export function getDocumentStatusFieldWrite(status: GQL.DocumentStatus): DocumentStatusFieldWrite {
  switch (status) {
    case GQL.DocumentStatus.Draft:
      return {
        isActive: true,
        isSigned: false,
        status,
        variant: GQL.DocumentVariant.Document,
      };
    case GQL.DocumentStatus.Signed:
      return {
        isActive: true,
        isSigned: true,
        status,
        variant: GQL.DocumentVariant.Document,
      };
    case GQL.DocumentStatus.Expired:
      return { isActive: false, isSigned: null, status, variant: null };
    case GQL.DocumentStatus.Template:
      return {
        isActive: true,
        isSigned: false,
        status,
        variant: GQL.DocumentVariant.Template,
      };
  }
}

export function getDocumentStatusChangeEligibility(args: {
  document: DocumentStatusSubject;
  isEditor: boolean;
  mimetype: string | null;
  targetStatus: GQL.DocumentStatus;
  workflow: DocumentStatusWorkflowContext | null;
}): DocumentStatusChangeEligibility {
  const { document, isEditor, mimetype, targetStatus, workflow } = args;
  if (getComputedDocumentStatus(document) === targetStatus) return { outcome: 'noop' };
  if (!isEditor) return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.NotAuthorized };

  switch (targetStatus) {
    case GQL.DocumentStatus.Draft:
      return getDraftEligibility({ document, mimetype, workflow });
    case GQL.DocumentStatus.Signed:
      return getSignedEligibility({ document, mimetype, workflow });
    case GQL.DocumentStatus.Expired:
      return getExpiredEligibility({ document, mimetype, workflow });
    case GQL.DocumentStatus.Template:
      return getTemplateEligibility({ document, workflow });
  }
}

export function getDocumentStatusSkipMessage(reason: DocumentStatusSkipReason): string {
  switch (reason) {
    case DOCUMENT_STATUS_SKIP_REASON.AttachedToWorkflow:
      return 'This document is a signing file in a workflow.';
    case DOCUMENT_STATUS_SKIP_REASON.CannotSignTemplate:
      return 'Templates cannot be marked signed.';
    case DOCUMENT_STATUS_SKIP_REASON.NotAuthorized:
      return 'You need edit access to change this status.';
    case DOCUMENT_STATUS_SKIP_REASON.NotPdf:
      return 'Only PDFs can change to this status.';
    case DOCUMENT_STATUS_SKIP_REASON.SignedOrCertificate:
      return 'Signed documents and certificates cannot change to this status.';
    case DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline:
      return `This document is a signing file in an Aline workflow and it's status is automatically set by the workflow.`;
    case DOCUMENT_STATUS_SKIP_REASON.WorkflowSentForSignature:
      return 'This document was already sent for signature.';
  }
}

/**
 * A signing file's workflow reason wins over the template and PDF reasons, since the workflow
 * is what controls the document's status.
 */
export function getManualSignedSkipReason(args: {
  document: Pick<DocumentStatusSubject, 'variant'>;
  mimetype: string | null;
  workflow: DocumentSigningWorkflowContext | null;
}): DocumentStatusSkipReason | null {
  const { document, mimetype, workflow } = args;
  const signingWorkflow = getSigningFileWorkflow(workflow);
  if (signingWorkflow && !signingWorkflow.isSignedOutsideAline)
    return DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline;
  if (signingWorkflow?.wasSentForSig) return DOCUMENT_STATUS_SKIP_REASON.WorkflowSentForSignature;
  if (document.variant === GQL.DocumentVariant.Template)
    return DOCUMENT_STATUS_SKIP_REASON.CannotSignTemplate;
  if (mimetype !== MimeType.PDF) return DOCUMENT_STATUS_SKIP_REASON.NotPdf;

  return null;
}

/**
 * Reactivating an expired, already-signed document only flips `isActive` back, so it skips the
 * manual-signed rule (and its PDF check). A signing file still waits until signing is complete.
 */
function getSignedEligibility(args: {
  document: DocumentStatusSubject;
  mimetype: string | null;
  workflow: DocumentStatusWorkflowContext | null;
}): DocumentStatusChangeEligibility {
  const { document, mimetype, workflow } = args;
  if (document.isSigned && document.variant === GQL.DocumentVariant.Document) {
    const signingWorkflow = getSigningFileWorkflow(workflow);
    if (!signingWorkflow || getIsSigningComplete(signingWorkflow)) return { outcome: 'apply' };
  }

  return eligibilityFromSignedRule(getManualSignedSkipReason({ document, mimetype, workflow }));
}

/**
 * A signing file waits until its workflow finishes signing, and that reason wins. After that,
 * only PDFs can be expired from draft, since only PDFs can be marked signed by hand. A document
 * that is already signed (by a workflow, possibly an Aline document) can be expired whatever
 * its format.
 */
function getExpiredEligibility(args: {
  document: DocumentStatusSubject;
  mimetype: string | null;
  workflow: DocumentStatusWorkflowContext | null;
}): DocumentStatusChangeEligibility {
  const { document, mimetype, workflow } = args;
  if (getIsWorkflowControllingStatus(workflow))
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline };
  const isSigned = document.isSigned || document.isCertificate;
  if (!isSigned && mimetype !== MimeType.PDF)
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.NotPdf };

  return { outcome: 'apply' };
}

function getDraftEligibility(args: {
  document: DocumentStatusSubject;
  mimetype: string | null;
  workflow: DocumentStatusWorkflowContext | null;
}): DocumentStatusChangeEligibility {
  const { document, mimetype, workflow } = args;
  if (document.isCertificate)
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SignedOrCertificate };
  if (document.variant === GQL.DocumentVariant.Template) {
    if (getSigningFileWorkflow(workflow))
      return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline };
    return { outcome: 'apply' };
  }
  if (!document.isSigned) return { outcome: 'apply' };

  return eligibilityFromSignedRule(getManualSignedSkipReason({ document, mimetype, workflow }));
}

function getTemplateEligibility(args: {
  document: DocumentStatusSubject;
  workflow: DocumentStatusWorkflowContext | null;
}): DocumentStatusChangeEligibility {
  const { document, workflow } = args;
  if (getIsWorkflowControllingStatus(workflow))
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline };
  if (document.isSigned || document.isCertificate)
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SignedOrCertificate };
  if (getSigningFileWorkflow(workflow))
    return { outcome: 'skip', reason: DOCUMENT_STATUS_SKIP_REASON.SigningWithinAline };

  return { outcome: 'apply' };
}

function getSigningFileWorkflow<TWorkflow extends DocumentSigningWorkflowContext>(
  workflow: TWorkflow | null
): TWorkflow | null {
  if (!workflow?.isSigningFile) return null;

  return workflow;
}

function getIsSigningComplete(workflow: DocumentStatusWorkflowContext): boolean {
  return IS_SIGNING_COMPLETE_BY_ENVELOPE_STATUS[workflow.envelopeStatus];
}

// A signing file whose workflow hasn't finished signing: the workflow owns its status.
function getIsWorkflowControllingStatus(workflow: DocumentStatusWorkflowContext | null): boolean {
  const signingWorkflow = getSigningFileWorkflow(workflow);
  if (!signingWorkflow) return false;
  return !getIsSigningComplete(signingWorkflow);
}

function eligibilityFromSignedRule(
  reason: DocumentStatusSkipReason | null
): DocumentStatusChangeEligibility {
  if (!reason) return { outcome: 'apply' };
  return { outcome: 'skip', reason };
}
